import * as React from 'react';

import cx from 'classnames';

/**
 * Truncates a filename while preserving the extension.
 * @param fileName - The full filename
 * @param maxLength - Maximum total length of the truncated filename
 * @returns The truncated filename with extension preserved
 */
function truncateFileName(fileName: string, maxLength: number): string {
  if (fileName.length <= maxLength) {
    return fileName;
  }

  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;

  if (!hasExtension) {
    // No extension, just truncate with ellipsis
    return fileName.substring(0, maxLength - 3) + '...';
  }

  const extension = fileName.substring(lastDotIndex);
  const name = fileName.substring(0, lastDotIndex);

  // Calculate how many characters we can keep from the name
  // Reserve space for: truncated name + "..." + extension
  const availableForName = maxLength - 3 - extension.length;

  if (availableForName <= 0) {
    // Extension is too long, just show truncated filename
    return fileName.substring(0, maxLength - 3) + '...';
  }

  return name.substring(0, availableForName) + '...' + extension;
}

/**
 * Props for the TruncatedFileName component.
 */
interface TruncatedFileNameProps {
  /** The full filename to display */
  fileName: string;
  /** Additional CSS classes */
  className?: string;
  /** Maximum number of characters to display (default: dynamically calculated) */
  maxChars?: number;
}

/**
 * Measures the width of text using a canvas, based on the element's computed font.
 */
function measureTextWidth(text: string, element: HTMLElement): number {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return text.length * 8; // Fallback

  const computedStyle = window.getComputedStyle(element);
  context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

  return context.measureText(text).width;
}

/**
 * Component that displays a filename with truncation, preserving the extension.
 * Uses string manipulation to truncate, ensuring reliable ellipsis display.
 * Dynamically calculates max characters based on container width and actual font.
 *
 * @example
 * ```tsx
 * <TruncatedFileName fileName="very_long_filename.pdf" className="text-sm font-medium" />
 * // Renders: "very_long_fil...pdf"
 * ```
 */
function TruncatedFileName({
  fileName,
  className,
  maxChars,
}: TruncatedFileNameProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [displayName, setDisplayName] = React.useState(fileName);
  const [isCalculated, setIsCalculated] = React.useState(false);

  // Calculate truncation based on available width
  const calculateTruncation = React.useCallback(() => {
    if (maxChars !== undefined) {
      return truncateFileName(fileName, maxChars);
    }

    if (!containerRef.current) {
      return fileName;
    }

    const container = containerRef.current;

    // Get available width from the container
    const availableWidth = container.clientWidth - 16;

    if (availableWidth <= 0) {
      return fileName;
    }

    // Check if full filename fits using canvas measurement
    const fullWidth = measureTextWidth(fileName, container);
    if (fullWidth <= availableWidth) {
      return fileName;
    }

    // Binary search to find the optimal truncation length
    const lastDotIndex = fileName.lastIndexOf('.');
    const hasExtension = lastDotIndex > 0;
    const extension = hasExtension ? fileName.substring(lastDotIndex) : '';
    const ellipsis = '...';

    let low = 1;
    let high = fileName.length;
    let result = fileName;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const truncated = truncateFileName(fileName, mid);
      const width = measureTextWidth(truncated, container);

      if (width <= availableWidth) {
        result = truncated;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    // Ensure we show at least some characters + ellipsis + extension
    const minLength = 4 + ellipsis.length + extension.length;
    if (result.length < minLength && fileName.length >= minLength) {
      result = truncateFileName(fileName, minLength);
    }

    // Final safety check: verify the result actually fits, if not reduce further
    let finalWidth = measureTextWidth(result, container);
    while (finalWidth > availableWidth && result.length > minLength) {
      const currentLength = result.length - extension.length - ellipsis.length;
      if (currentLength <= 1) break;
      result = truncateFileName(fileName, result.length - 1);
      finalWidth = measureTextWidth(result, container);
    }

    return result;
  }, [fileName, maxChars]);

  // Calculate after mount
  React.useEffect(() => {
    // Double RAF ensures layout is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDisplayName(calculateTruncation());
        setIsCalculated(true);
      });
    });
  }, [calculateTruncation]);

  // Recalculate on window resize (not container, to avoid feedback loop)
  React.useEffect(() => {
    if (!isCalculated) return;

    const handleResize = () => {
      // Reset to full filename first, then recalculate after layout
      setDisplayName(fileName);
      setIsCalculated(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setDisplayName(calculateTruncation());
          setIsCalculated(true);
        });
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateTruncation, isCalculated, fileName]);

  return (
    <div
      ref={containerRef}
      className={cx(
        'block flex-grow min-w-0 overflow-hidden whitespace-nowrap',
        className
      )}
      style={{
        // Hide content until calculated, but keep layout
        color: isCalculated ? undefined : 'transparent',
      }}
      title={fileName}
    >
      {displayName}
    </div>
  );
}

/**
 * Props for the FileName sub-component.
 * This component provides a container for the file name and related information.
 */
interface FileDropFileWrapperFileNameProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Sub-component for displaying the file name and related information.
 * This component provides a flex container that grows to fill available space.
 *
 * @example
 * ```tsx
 * <FileDropFileWrapper.FileName>
 *   <IconCheckmark />
 *   <div className="text-sm font-medium">document.pdf</div>
 * </FileDropFileWrapper.FileName>
 * ```
 */
function FileDropFileWrapperFileName({
  children,
  className,
}: FileDropFileWrapperFileNameProps) {
  return (
    <div className={cx('flex gap-2 flex-grow min-w-0 pl-2', className)}>
      {children}
    </div>
  );
}

/**
 * Props for the Actions sub-component.
 * This component provides a container for action buttons (e.g., remove, retry).
 */
interface FileDropFileWrapperActionsProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Sub-component for displaying action buttons.
 * This component provides a flex container for buttons like remove, retry, etc.
 *
 * @example
 * ```tsx
 * <FileDropFileWrapper.Actions>
 *   <IconButton appearance="ghost" onClick={handleRemove}>
 *     <IconX />
 *   </IconButton>
 * </FileDropFileWrapper.Actions>
 * ```
 */
function FileDropFileWrapperActions({
  children,
  className,
}: FileDropFileWrapperActionsProps) {
  return <div className={cx('flex gap-2', className)}>{children}</div>;
}

/**
 * Props for the main FileDropFileWrapper component.
 */
interface FileDropFileWrapperProps {
  /** Optional additional CSS classes to apply to the wrapper */
  className?: string;
  /** The file name element, must be a FileDropFileWrapper.FileName component */
  fileName: React.ReactElement<
    React.ComponentProps<typeof FileDropFileWrapperFileName>
  >;
  /** The actions element, must be a FileDropFileWrapper.Actions component */
  actions: React.ReactElement<
    React.ComponentProps<typeof FileDropFileWrapperActions>
  >;
  /** Optional progress indicator element, must be a FileDropFileWrapper.Progress component */
  progress?: React.ReactElement<
    React.ComponentProps<typeof FileDropFileWrapperProgress>
  >;
}

/**
 * A flexible wrapper component for displaying file upload information.
 * This component provides a consistent layout for file names, action buttons, and progress indicators.
 *
 * The component uses a compound component pattern with sub-components:
 * - `FileDropFileWrapper.FileName` - Container for file name and related info
 * - `FileDropFileWrapper.Actions` - Container for action buttons
 * - `FileDropFileWrapper.Progress` - Progress bar indicator
 *
 * @example
 * Basic usage with file name and actions:
 * ```tsx
 * <FileDropFileWrapper
 *   fileName={
 *     <FileDropFileWrapper.FileName>
 *       <div className="text-sm font-medium">document.pdf</div>
 *     </FileDropFileWrapper.FileName>
 *   }
 *   actions={
 *     <FileDropFileWrapper.Actions>
 *       <IconButton appearance="ghost" onClick={handleRemove}>
 *         <IconX />
 *       </IconButton>
 *     </FileDropFileWrapper.Actions>
 *   }
 * />
 * ```
 *
 * @example
 * With progress indicator:
 * ```tsx
 * <FileDropFileWrapper
 *   fileName={
 *     <FileDropFileWrapper.FileName>
 *       <div className="text-sm font-medium">document.pdf</div>
 *     </FileDropFileWrapper.FileName>
 *   }
 *   actions={
 *     <FileDropFileWrapper.Actions>
 *       <IconButton appearance="ghost" onClick={handleRemove}>
 *         <IconX />
 *       </IconButton>
 *     </FileDropFileWrapper.Actions>
 *   }
 *   progress={<FileDropFileWrapper.Progress progress={75} />}
 * />
 * ```
 *
 * @example
 * Success state with icon:
 * ```tsx
 * <FileDropFileWrapper
 *   fileName={
 *     <FileDropFileWrapper.FileName>
 *       <IconCheckmark />
 *       <div className="text-sm font-medium">document.pdf</div>
 *     </FileDropFileWrapper.FileName>
 *   }
 *   actions={
 *     <IconButton appearance="ghost" onClick={handleRemove}>
 *       <IconX />
 *     </IconButton>
 *   }
 * />
 * ```
 *
 * @example
 * Error state with retry button:
 * ```tsx
 * <FileDropFileWrapper
 *   fileName={
 *     <FileDropFileWrapper.FileName>
 *       <div className="text-sm font-medium text-red-500">document.pdf</div>
 *     </FileDropFileWrapper.FileName>
 *   }
 *   actions={
 *     <FileDropFileWrapper.Actions>
 *       <SmallButton appearance="text" onClick={handleRetry}>
 *         Retry
 *       </SmallButton>
 *       <IconButton appearance="ghost" onClick={handleRemove}>
 *         <IconX />
 *       </IconButton>
 *     </FileDropFileWrapper.Actions>
 *   }
 * />
 * ```
 */
function FileDropFileWrapper({
  className,
  fileName,
  actions,
  progress,
}: FileDropFileWrapperProps) {
  return (
    <div
      className={cx(
        'flex items-center gap-2 p-2 border border-solid border-grey-600 rounded-2',
        className
      )}
    >
      {fileName}
      {actions}
      {progress}
    </div>
  );
}

/**
 * Props for the Progress sub-component.
 */
interface FileDropFileWrapperProgressProps {
  /** Progress value as a percentage (0-100) */
  progress: number;
}

/**
 * Sub-component for displaying a progress bar.
 * This component renders an absolute-positioned progress bar at the bottom of the wrapper.
 * The progress bar animates smoothly when the progress value changes.
 *
 * @example
 * ```tsx
 * <FileDropFileWrapper.Progress progress={50} />
 * ```
 */
function FileDropFileWrapperProgress({
  progress,
}: FileDropFileWrapperProgressProps) {
  return (
    <div
      className="absolute bottom-0 left-0 h-1 bg-cyan-600 transition-all duration-300"
      style={{ width: `${progress}%`, borderRadius: '2px' }}
    />
  );
}

FileDropFileWrapper.FileName = FileDropFileWrapperFileName;
FileDropFileWrapper.Actions = FileDropFileWrapperActions;
FileDropFileWrapper.Progress = FileDropFileWrapperProgress;
FileDropFileWrapper.TruncatedFileName = TruncatedFileName;

export { FileDropFileWrapper };
